import React, { useEffect, useRef, useState } from "react";
import { Text } from "../../../components/components";
import { Button, Spacer } from "../../../components";
import { Animated, StyleSheet, View } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { mixActions, useAppDispatch } from "../../../store";
import ConfettiCannon from "react-native-confetti-cannon";
import { Outlines } from "../../../theme";

export interface PopupProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  activateConfetti?: boolean;
  onClose?: () => void;
  timerDuration?: number;
}

export const Popup = ({
  icon,
  title,
  children,
  activateConfetti = true,
  onClose,
  timerDuration = 60 * 5,
}: PopupProps) => {
  // Animated.Value ist "animierbarer Wert"
  const translateY = useRef(new Animated.Value(-900)).current; // startet über Screen
  const confettiOpacity = useRef(new Animated.Value(1)).current;

  const dispatch = useAppDispatch();
  const [timeLeft, setTimeLeft] = useState(timerDuration);
  const [showConfetti, setShowConfetti] = useState(false);

  const locationsLeft = () => {
    const right = (timerDuration - timeLeft) / timerDuration;
    const left = right == 1 ? 1 : Math.max(0, right - 0.05);
    return [left, right];
  };

  const handleClose = () => {
    // Reverse:
    Animated.timing(translateY, {
      toValue: -900,
      useNativeDriver: true,
      duration: 200,
    }).start(() => {
      // Callback nach Animation
      if (onClose) onClose();
      dispatch(mixActions.hidePopup());
    });
  };

  useEffect(() => {
    // spring: federnde Bewegung || timing: linear
    Animated.spring(translateY, {
      // Popup fällt rein
      toValue: 0,
      useNativeDriver: true,
      damping: 8, // ++ => Bewegung stoppt schneller, weniger Trampolin-Federn
      stiffness: 80, // ++ Bewegung zügiger
    }).start();

    // Timer starten
    let interval: NodeJS.Timeout | undefined;
    setTimeLeft(timerDuration);
    interval = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 0.1) {
          return 0;
        }
        return prev - 0.1;
      });
    }, 100);

    const confettiTimer = setTimeout(() => {
      setShowConfetti(true);

      Animated.timing(confettiOpacity, {
        toValue: 0,
        useNativeDriver: true,
        duration: 3000,
      }).start(() => setShowConfetti(false));
    }, 1000);
  }, []);

  return (
    <Animated.View
      style={[{ transform: [{ translateY }] }, styles.wrapperContainer]}
    >
      {activateConfetti && showConfetti && (
        <Animated.View style={[{ opacity: confettiOpacity }, styles.confetti]}>
          <ConfettiCannon count={50} origin={{ x: -10, y: 0 }} />
        </Animated.View>
      )}

      <div style={styles.backdrop} />

      <div style={styles.actualPopup}>
        <Button style={styles.iconButton} onPress={() => handleClose()}>
          {icon}
        </Button>

        <Text style={styles.title}>{title}</Text>

        {children}

        <Spacer size="large" />

        <LinearGradient
          style={styles.gradientContainer}
          colors={["#0000ff50", "transparent"]}
          locations={locationsLeft()}
          start={[0, 0]}
          end={[0, 0]}
        >
          <Button
            disabled={timeLeft > 0}
            onPress={() => handleClose()}
            style={styles.timerButton}
          >
            {timeLeft > 60 ? (
              <Text>Warte noch {Math.round(timeLeft / 60)}min</Text>
            ) : timeLeft > 0 ? (
              <Text>Warte noch {timeLeft.toFixed(0)}s</Text>
            ) : (
              <Text style={{ fontWeight: "bold" }}>
                Klicke hier, sobald du fertig bist
              </Text>
            )}
          </Button>
        </LinearGradient>
      </div>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  confetti: {
    position: "absolute",
    zIndex: 2,
    left: "-100%",
    bottom: "-50%",
  },
  wrapperContainer: {
    position: "absolute",
    zIndex: 1,
    top: "1%",
    left: "1%",
    right: "1%",
    bottom: "1%",
  },
  backdrop: {
    borderRadius: Outlines.borderRadius.base,
    backdropFilter: "blur(5px)",
    position: "absolute",
    zIndex: 1,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  actualPopup: {
    position: "absolute",
    zIndex: 3,
    top: "8%",
    left: "5%",
    right: "5%",
    bottom: "8%",
    backgroundColor: "#ffffff30",
    backdropFilter: "blur(10px)",
    boxShadow:
      "10px 10px 20px rgba(0, 0, 0, 0.2), 10px 10px 20px #ffffffA0 inset",
    border: "2px solid white",
    borderRadius: 50,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  iconButton: {
    marginBottom: 50,
    transform: [{ scale: 2 }],
  },
  title: {
    fontSize: 30,
    fontWeight: "bold",
    marginBottom: 50,
    marginHorizontal: 20,
    textAlign: "center",
  },
  gradientContainer: {
    width: "80%",
    alignSelf: "center",
    borderRadius: 50,
    borderWidth: 1,
    borderColor: "#0000ff",
  },
  timerButton: {
    height: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
});
